import React, { useCallback, useEffect, useRef, useState } from "react";
import service from "../../../store/services/user.service";
import clientService from "../../../store/services/clients.service";
import portfolioService from "../../../store/services/portfolio.service";
import programService from "../../../store/services/programs.service";
import InputUnderline from "../../../components/controls/input/InputUnderline";
import Searchable from "../../../components/controls/select/CustomSelect";
import Input from "../../../components/controls/input/customInput/CustomInput";
import { AddIconSvg, CalendarIcon } from "../../../modules/default-svg";
import Button from "../../../components/controls/button";
import {
  AvatarGroup,
  AvatarGroupItem,
  AvatarGroupPopover,
  partitionAvatarGroupItems,
} from "@fluentui/react-components";
import { apiCall } from "../../../store/services/service";
import { URL_SEARCH_CLIENT_PORTFOLIO } from "../../../store/services/URL";
import { ColumnConfigProvider } from "../../../components/controls/tables/ColumnConfigProvider";
import { useDispatch } from "react-redux";
import { generalActions } from "../../../store/actions";
import DeleteRequest from "../../../components/DeleteRequest/DeleteRequest";
import Table from "../../../components/controls/tables/Table";
import { calculatePageSize, UserSearchRefType } from "../../../helpers/utils";
import RichTextEditor from "../../../components/richTextEditor/RichTextEditor";
import EntityFileUpload from "../../../components/controls/fileUpload/EntityFileUpload";
import { isDateGreaterThan } from "../../../utils/systemSettings";
import UserSelect from "../../../components/userSelect/UserSelect";

const Programs = (props) => {
  const {
    clientID,
    defaultClient,
    defaultPortfolio,
    portfolioID,
    allPrograms,
    pageLoading,
    loading,
    pagination,
    getProgramsRequest,
    createProgramsRequest,
    updateProgramsRequest,
    error,
    overviewType,
  } = props;
  const permissions = localStorage.getItem("permissions");
  const prevClientId = useRef(null);
  const manualUpdate = useRef({
    client: false,
    portfolio: false,
  });
  const [isModalLoading, setIsModalLoading] = useState(false);
  useEffect(() => {
    setIsModalLoading(loading);
  }, [loading]);
  const [data, setData] = useState([]);
  const [pageNum, setPageNum] = useState(1);
  const [perPageItems] = useState(calculatePageSize());
  const [ProgramName, setProgramName] = useState("");
  const [budget, setBudget] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState("");
  const [objectives, setObjectives] = useState("");
  const [stakeholders, setStakeholders] = useState("");
  const [refId, setRefId] = useState("");
  const [manager, setManager] = useState(null);
  const [clientOptions, setClientOptions] = useState([]);
  const [client, setClient] = useState(null);
  const [portfolioOptions, setPortfolioOptions] = useState([]);
  const [portfolio, setPortfolio] = useState(null);
  const [industries, setIndustries] = useState([]);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [selectedRow, setSelectedRow] = useState({});
  const [isCreate, setIsCreate] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isHeader, setIsHeader] = useState(true);
  const [parentType, setParentType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [formErrors, setFormErrors] = useState({
    name: "",
    startDate,
    endDate,
    clientId: "",
  });
  const [isCreateNewRow, setIsCreateNewRow] = useState(false);
  const [deleteId, setDeletedId] = useState(null);
  const [deleteRequestModal, setDeleteRequestModal] = useState(false);
  const [deleteRequestData, setDeleteRequestData] = useState({});
  const [deleteModal, setDeleteModal] = useState(null);
  const dispatch = useDispatch();
  const [tableSearch, setTableSearch] = useState(null);

  const fetchData = (pageNumber, pageSize, searchTerm, forceClearSearch = false) => {
    const payload = { page: pageNumber ?? pageNum, size: pageSize ?? perPageItems, search: forceClearSearch ? null : (searchTerm ?? tableSearch) };
    payload.parent = parentType;

    if (clientID) {
      payload.id = clientID;
      payload.parent = "Client";
    }

    if (portfolioID) {
      payload.id = portfolioID;
      payload.parent = "Portfolio";
    }
    getProgramsRequest(payload);
  };

  const searchClientHandler = (value) => {
    if (value === "") {
      setClientOptions([]);
      return;
    }
    clientService
      .searchClientsRequest(value)
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          label: obj.name,
          value: obj.id,
        }));
        setClientOptions(transformedArray);
      })
      .catch(() => {
        setClientOptions([]);
      });
  };

  const searchPortfolioHandler = (value) => {
    if (value === "") {
      setPortfolioOptions([]);
      return;
    }
    const payload = { id: client?.value, searchText: value };
    let queryParam = `?searchTerm=${payload.searchText}`;
    if (client)
      queryParam = `?clientId=${payload.id}&searchTerm=${payload.searchText}`;
    apiCall
      .get({
        url: `${URL_SEARCH_CLIENT_PORTFOLIO}${queryParam}`,
        isAuthToken: true,
      })
      .then((res) => {
        const transformedArray = res.map((obj) => ({
          label: obj.name,
          value: obj.id,
        }));
        setPortfolioOptions(transformedArray);
      })
      .catch(() => {
        setPortfolioOptions([]);
      });
  };

  // Fetches at most once per selected client - CustomSelect's onFocus can fire
  // more than once per interaction (it programmatically calls openMenu()), and
  // react-select's onInputChange also fires on blur/menu-close, so this cache
  // is the actual guard against repeat calls, not the actionMeta check below.
  const fetchedIndustriesClientIdRef = useRef(null);
  const fetchClientIndustries = (clientId) => {
    if (!clientId) {
      fetchedIndustriesClientIdRef.current = null;
      setIndustryOptions([]);
      return;
    }
    if (fetchedIndustriesClientIdRef.current === clientId) {
      return;
    }
    fetchedIndustriesClientIdRef.current = clientId;
    clientService
      .getClientIndustriesRequest(clientId)
      .then((res) => {
        const transformed = (res || []).map((obj) => ({ label: obj.name, value: obj.id }));
        setIndustryOptions(transformed);
      })
      .catch(() => {
        fetchedIndustriesClientIdRef.current = null;
        setIndustryOptions([]);
      });
  };

  const handleIndustrySearch = (value, actionMeta) => {
    if (value !== null && actionMeta && actionMeta.action !== "input-change") {
      return;
    }
    fetchClientIndustries(client?.value);
  };

  const handleIndustriesChange = (ids) => {
    const pool = [...industries, ...industryOptions.map((o) => ({ id: o.value, name: o.label }))];
    const selected = ids.map((id) => pool.find((item) => item.id === id)).filter(Boolean);
    setIndustries(selected);
  };

  const handleSave = () => {
    const errors = {};
    if (!ProgramName) errors["name"] = `Name Field is Required.`;
    if (!clientID && !client) errors["clientId"] = `Client Field is Required.`;
    if (startDate && endDate && isDateGreaterThan(startDate, endDate)) {
      errors[
        "endDate"
      ] = `End date must be equal to or greater than start date.`;
    }
    setFormErrors(errors);
    if (Object.keys(errors).length) {
      return;
    }
    const payload = {
      budget: budget || 0,
      clientId: client?.value,
      endDate: endDate || null,
      name: ProgramName,
      objectives: objectives,
      portfolioId: portfolio?.value,
      refId: refId,
      stakeholderList: stakeholders,
      startDate: startDate || null,
      status: status,
      ownerId: manager?.id,
      industryIds: industries.map((i) => i.id),
    };

    const data = { payload };
    data.parent = parentType;
    if (clientID) {
      data.parentId = clientID;
      data.parent = "Client";
    }
    if (portfolioID) {
      data.parentId = portfolioID;
      data.parent = "Portfolio";
    }
    if (Object.keys(selectedRow).length > 0) {
      data.id = selectedRow.id;
      updateProgramsRequest(data);
    } else {
      createProgramsRequest(data);
    }
  };

  useEffect(() => {
    if (clientID || portfolioID) {
      // Reset form states when switching contexts
      setSelectedRow({});
      setIsEdit(false);
      setIsCreate(false);
      setFormErrors({});
      setProgramName("");
      setBudget("");
      setStartDate("");
      setEndDate("");
      setStatus("");
      setObjectives("");
      setStakeholders("");
      setRefId("");
      setManager(null);
      setClientOptions([]);
      setPortfolioOptions([]);
      setIndustries([]);

      // Reset client and portfolio to defaults
      if (defaultClient) {
        setClient({ value: defaultClient.id, label: defaultClient.name });
      } else {
        setClient(null);
      }

      if (defaultPortfolio) {
        setPortfolio({
          value: defaultPortfolio.id,
          label: defaultPortfolio.name,
        });
      } else {
        setPortfolio(null);
      }
    }
  }, [clientID, portfolioID, defaultClient, defaultPortfolio]);

  useEffect(() => {
    if (ProgramName) {
      setFormErrors({});
    }
  }, [ProgramName]);

  const fetchProgramData = async () => {
    setIsLoading(true);
    programService
      .fetchProgramDataRequest({ id: selectedRow.id })
      .then((data) => {
        setProgramName(data.name);
        setBudget(data.budget);
        setStartDate(data.startDate);
        setEndDate(data.endDate);
        setStatus(data.status);
        setObjectives(data.objectives);
        setStakeholders(data.stakeholderList);
        setRefId(data.refId);
        setAttachments(data.attachments || []);
        setPortfolio(data.portfolio);
        setManager(data.owner);
        setIndustries(data.industries || []);
        if (data.client) {
          const manArr = { value: data.client.id, label: data.client.name };
          setClient(manArr);
        } else {
          setClient(null);
        }
        if (data.portfolio) {
          const manArr = {
            value: data.portfolio.id,
            label: data.portfolio.name,
          };
          setPortfolio(manArr);
        } else {
          setPortfolio(null);
        }
        // setTimeout(() => {
        setIsLoading(false);
        // }, 3000);
      })
      .catch((error) => console.error("Error fetching data:", error));
  };

  useEffect(() => {
    if (defaultClient) {
      setClient({ value: defaultClient.id, label: defaultClient.name });
    }
  }, [defaultClient]);

  useEffect(() => {
    if (defaultPortfolio) {
      setPortfolio({
        value: defaultPortfolio.id,
        label: defaultPortfolio.name,
      });
    }
  }, [defaultPortfolio]);

  useEffect(() => {
    if (Object.keys(selectedRow).length > 0) {
      isEdit && fetchProgramData();
    } else {
      setProgramName("");
      setBudget("");
      setStartDate("");
      setEndDate("");
      setStatus("");
      setObjectives("");
      setStakeholders("");
      setRefId("");
      setManager(null);
      setIndustries([]);
      defaultClient
        ? setClient({ value: defaultClient.id, label: defaultClient.name })
        : setClient("");
      defaultPortfolio
        ? setPortfolio({
          value: defaultPortfolio.id,
          label: defaultPortfolio.name,
        })
        : setPortfolio("");
      setPortfolioOptions([]);
    }
  }, [selectedRow, isEdit]);

  useEffect(() => {
    if (error) {
      setFormErrors({ name: error });
    } else {
      setFormErrors({});
    }
  }, [error]);

  const handleSearch = (filteredData) => {
    setData(filteredData);
  };

  const formBody = () => {
    const { inlineItems, overflowItems } = partitionAvatarGroupItems({
      // items: assignedResources,
      items: [],
    });
    return (
      <div>
        <InputUnderline
          placeholder="Add program name"
          value={ProgramName}
          error={formErrors.name}
          handleChange={setProgramName}
          isRequired
          loading={isLoading}
        />
        <div className="d-flex mb-3">
          {overviewType !== "Client" && overviewType !== "Portfolio" && (
            <div className="mt-4 w-50">
              <Searchable
                label="Client"
                SelectText="client"
                selectWd="me-3"
                searchText={searchClientHandler}
                options={clientOptions}
                defaultVal={client}
                onChange={(e) => {
                  setClient(e);
                  setManager(null);
                  setPortfolio("");
                  setIndustries([]);
                }}
                errorMessage={formErrors.clientId}
                isRequired
                loading={isLoading}
              />
            </div>
          )}
          {overviewType !== "Portfolio" && (
            <div className="mt-4 w-50">
              <Searchable
                label="Portfolio"
                CustomClass="ModalAvatarSearch"
                persona={true}
                searchText={searchPortfolioHandler}
                SelectText="portfolio"
                options={portfolioOptions}
                defaultVal={portfolio}
                onChange={(e) => setPortfolio(e)}
                loading={isLoading}
              />
            </div>
          )}
        </div>
        <div className="ModalAvatarGroup">
          <div className="row align-items-end gx-3">
            <div className="col-4">
              <UserSelect
                refType={UserSearchRefType.Client}
                refId={client?.value}
                label="Program Manager"
                buttonLabel={"Select Manager"}
                value={manager}
                onChange={setManager}
                loading={isLoading}
                disabled={!client}
              />
            </div>
            <div className="col-4">
              <Searchable
                label="Industry"
                SelectText="industry"
                multiselect={true}
                creatable={false}
                searchText={handleIndustrySearch}
                options={industryOptions}
                defaultVal={industries.map((i) => ({ label: i.name, value: i.id }))}
                onChange={handleIndustriesChange}
                isDisabled={!client?.value}
                loading={isLoading}
              />
            </div>
            <div className="col-8">
              {inlineItems && (
                <div className="AvatarGroupWrapper">
                  <AvatarGroup layout="stack">
                    {inlineItems.map((item) => (
                      <AvatarGroupItem
                        size={28}
                        name={item.label}
                        image={{ src: item.image ? item.image : "" }}
                        key={item.name}
                      />
                    ))}
                    {overflowItems && (
                      <AvatarGroupPopover>
                        {overflowItems.map((item) => (
                          <AvatarGroupItem
                            name={item.label}
                            image={{ src: item.image ? item.image : "" }}
                            key={item.name}
                          />
                        ))}
                      </AvatarGroupPopover>
                    )}
                  </AvatarGroup>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="row align-items-center gx-3 mb-3">
          <div className="col-4">
            <Input
              icon={false}
              value={refId}
              type="text"
              placeholder="Enter reference ID"
              handleChange={setRefId}
              label="Reference ID"
              loading={isLoading}
            />
          </div>
          <div className="col-4">
            <Input
              icon={<CalendarIcon />}
              value={startDate}
              maxDate={endDate}
              type="date"
              handleChange={setStartDate}
              label="Start Date"
              errorMessage={formErrors.startDate}
              loading={isLoading}
            />
          </div>
          <div className="col-4">
            <Input
              icon={<CalendarIcon />}
              value={endDate}
              minDate={startDate}
              type="date"
              handleChange={setEndDate}
              label="End Date"
              errorMessage={formErrors.endDate}
              loading={isLoading}
            />
          </div>
        </div>
        <div className="row align-items-center gx-3 mb-3">
          <div className="col-4">
            <Input
              icon={false}
              placeholder="Enter status"
              value={status}
              type="tesxt"
              handleChange={setStatus}
              label="Status"
              loading={isLoading}
            />
          </div>
          {isEdit && (
            <div className="col-4">
              <Input
                icon={false}
                // placeholder={`${getAccountCurrency()} 0`}
                value={budget}
                type="number"
                currency={true}
                placeholder="0"
                isDisabled={true}
                handleChange={setBudget}
                label="Budget"
                loading={isLoading}
              />
            </div>
          )}
        </div>
        <div className="mb-3">
          <RichTextEditor
            label="Objectives"
            content={objectives}
            onChange={setObjectives}
            loading={isLoading}
          />
        </div>
        <div className="mb-3">
          <RichTextEditor
            label="Stakeholders"
            content={stakeholders}
            onChange={setStakeholders}
            loading={isLoading}
          />
        </div>

        <EntityFileUpload
          entityId={selectedRow.id}
          entityType="Program"
          attachments={attachments}
          onAttachmentsChange={() => {
            // Refresh program data to get updated attachments
            if (selectedRow.id) {
              service
                .fetchProgramDataRequest({ id: selectedRow.id })
                .then((programData) => {
                  setAttachments(programData.attachments);
                })
                .catch((error) => {
                  console.error("Error fetching program data:", error);
                });
            }
          }}
          disabled={isLoading || !selectedRow.id}
        />
      </div>
    );
  };

  const handleDelete = (selectedRow) => {
    // const id = JSON.parse(localStorage.getItem("selectedRow")).id;
    const id = selectedRow.id;
    setIsModalLoading(true);
    programService
      .deleteProgramsRequest(id)
      .then((response) => {
        if (response && !response.IsDeletionAllowed) {
          setDeleteRequestData(response);
          setDeleteRequestModal(true);
        } else {
          dispatch(
            generalActions.showToast({
              title: "Progran deleted successfully.",
              type: "success",
            })
          );
          // localStorage.removeItem("selectedRow");
          setDeletedId(id);
          fetchData();
        }
      })
      .catch((err) => {
        dispatch(
          generalActions.showToast({
            title: "Something went wrong. Please try again later.",
            type: "error",
          })
        );
      })
      .finally(() => {
        setSelectedRow({});
        setDeleteModal(false);
        setIsModalLoading(false);
      });
  };

  const handleDeleteRequested = () => {
    fetchData();
  };
  const handleCreate = () => {
    setIsCreate(true);
  };

  const handleCancel = () => {
    setFormErrors({});
    setIsEdit(false);
    setProgramName("");
    setBudget("");
    setStartDate("");
    setEndDate("");
    setStatus("");
    setObjectives("");
    setStakeholders("");
    setRefId("");
    setManager(null);
    setClient("");
    setPortfolioOptions("");
    setPortfolio("");
    setIndustries([]);
  };

  const handleCancelAddRow = () => {
    setIsCreateNewRow(false);
    fetchData();
  };

  useEffect(() => {
    fetchData();
  }, [pageNum, perPageItems]);

  const handleAddRow = useCallback(() => {
    setIsCreateNewRow(true);
    fetchData(pagination.TotalPages ?? 1, perPageItems > 10 ? perPageItems - 5 : perPageItems - 3);
  }, []);

  useEffect(() => {
    if (clientID) {
      setParentType("Client");

      setIsHeader(false);
    }
    if (portfolioID) {
      setParentType("Portfolio");
      setIsHeader(false);
    }
    setData([]);
    setPageNum(1);
    setDeletedId(null);
  }, [portfolioID, clientID]);

  useEffect(() => {
    if (allPrograms && allPrograms.length > 0) {
      if (isCreateNewRow) {
        setData(
          allPrograms.sort((a, b) => {
            return b.isFlagged - a.isFlagged;
          })
        );
        setIsCreateNewRow(false);
      } else {
        if (pageNum === 1) {
          setData(
            allPrograms
              .filter((item) => item.id !== deleteId)
              .sort((a, b) => {
                return b.isFlagged - a.isFlagged;
              })
          );
        } else {
          setData((prevData) => {
            const newData = [...prevData, ...allPrograms].reduce(
              (unique, item) => {
                return unique.some(
                  (allocatedResource) => allocatedResource.id === item.id
                )
                  ? unique
                  : [...unique, item];
              },
              []
            );
            return newData
              .filter((item) => item.id !== deleteId)
              .sort((a, b) => {
                return b.isFlagged - a.isFlagged;
              });
          });
        }
      }
    } else if (allPrograms && allPrograms.length === 0 && pageNum === 1) {
      setData([]);
    }
  }, [allPrograms, deleteId, pageNum, isCreateNewRow]);

  useEffect(() => {
    if (portfolio && !manualUpdate.current.client) {
      if (!client) {
        portfolioService
          .getPortfolioDetails({ id: portfolio.value })
          .then((portfolioData) => {
            manualUpdate.current.client = true;
            portfolioData.client
              ? setClient({
                label: portfolioData.client.name,
                value: portfolioData.client.id,
              })
              : setClient("");
          })
          .catch((error) =>
            console.error("Error fetching portfolio data:", error)
          );
      }
    }
  }, [portfolio]);

  useEffect(() => {
    if (!client) {
      setPortfolio("");
    }
    if (client) {
      prevClientId.current = client.value;
    }
    // if (ClientId && prevClientId.current === null) {
    //   prevClientId.current = ClientId.value;
    // }
    if (client && prevClientId.current !== client.value) {
      setPortfolio("");
      prevClientId.current = client.value;
    }
    manualUpdate.current.client = false;
    manualUpdate.current.portfolio = false;
  }, [client]);

  const handleSearchTrigger = (searchTerm) => {
    if ((!tableSearch && !searchTerm) || (tableSearch === searchTerm)) return;

    let pageNumber = pageNum;
    if (!tableSearch || (tableSearch && !searchTerm) || (tableSearch !== searchTerm)) {
      setData([]); //clear existing data
      pageNumber = 1; //reset to first page
    }

    setTableSearch(searchTerm);
    fetchData(pageNumber, perPageItems, searchTerm, !searchTerm);
  };

  return (
    <>
      <ColumnConfigProvider
        tableName={parentType ? `${parentType}-Program` : "Program"}
      >
        <div className="listingScreen position-relative">
          {isHeader && (
            <div className="db_Subheader d-flex justify-content-between align-items-center mb-4">
              <h4>Programs</h4>
              {permissions?.includes("Program.Create") && (
                <Button
                  btnClass="d_btn d_btn_primary"
                  icon={<AddIconSvg />}
                  label="Add Program"
                  clickHandler={handleCreate}
                />
              )}
            </div>
          )}
          <Table
            type="Program"
            isHeader={isHeader}
            parentType={parentType}
            rows={data || []}
            searchHandler={handleSearch}
            body={formBody()}
            saveBtnLabel="Save"
            cancelBtnLabel="Cancel"
            setPageNum={setPageNum}
            perPageItems={perPageItems}
            setIsCreate={setIsCreate}
            handleSave={() => handleSave()}
            handleDelete={(selectedRow) => handleDelete(selectedRow)}
            handleCancel={() => handleCancel()}
            setIsEdit={setIsEdit}
            loading={pageLoading}
            modalLoading={isModalLoading}
            isCreate={isCreate}
            forceShowDeleteModal={deleteModal}
            pagination={pagination}
            setSelectedRow={setSelectedRow}
            clientId={clientID}
            portfolioId={portfolioID}
            handleAddRow={handleAddRow}
            handleCancelAddRow={handleCancelAddRow}
            setData={setData}
            triggerSearch={handleSearchTrigger}
          />
        </div>
      </ColumnConfigProvider>
      {deleteRequestModal && (
        <DeleteRequest
          data={deleteRequestData}
          handleCancel={setDeleteRequestModal}
          handleRefresh={() => handleDeleteRequested()}
        />
      )}
    </>
  );
};

export default Programs;
